import React from 'react';
import {
  ArrayField,
  BooleanField,
  BooleanInput,
  ChipField,
  Create,
  Edit,
  EditButton,
  Filter,
  FormDataConsumer,
  maxLength,
  minLength,
  NullableBooleanInput,
  number,
  NumberField,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  SearchInput,
  SingleFieldList,
  TextField,
  TextInput,
} from 'react-admin';

import withStyles from '@material-ui/core/styles/withStyles';
import { ZippityDatagrid, ZippitySimpleForm } from '../../components';
import ConditionOptionsChipField from './ConditionOptionsChipField';
import BulkDeleteButton from '../../components/BulkDeleteButton';
import CustomTitle from '../../components/CustomTitle';
import ReferenceArrayChild from '../../components/ReferenceArrayChild';
import { perPage } from '../../helper';
import ReferenceChild from '../../components/ReferenceChild';
import ListWithCSVImport from '../../components/ListWithCSVImport';

const InspectionFilter = (props) => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
    <NullableBooleanInput source="condition_required" defaultValue />
    <NullableBooleanInput source="photo_required" defaultValue />
    <NullableBooleanInput source="before_after_photo" defaultValue />
    <NullableBooleanInput source="overall_measurement" defaultValue />
    <NullableBooleanInput source="front_driver" defaultValue />
    <NullableBooleanInput source="front_passenger" defaultValue />
    <NullableBooleanInput source="rear_driver" defaultValue />
    <NullableBooleanInput source="rear_passenger" defaultValue />
    <NullableBooleanInput source="notes_required" defaultValue />
    <NullableBooleanInput source="is_hidden" defaultValue />
  </Filter>
);

const listStyles = {
  nb_commands: { color: 'purple' },
};

export const InspectionList = withStyles(listStyles)(
  ({ classes, ...props }) => (
    <div>
      <ListWithCSVImport
        {...props}
        filters={<InspectionFilter />}
        bulkActionButtons={<BulkDeleteButton />}
        sort={{ field: 'display_order', order: 'ASC' }}
        perPage={50}
      >
        <ZippityDatagrid>
          <TextField source="created_at" />
          <TextField source="modified_at" />
          <TextField source="inspection_name" />
          <NumberField source="display_order" />

          <TextField source="measurement_unit" />

          <BooleanField source="condition_required" />
          <BooleanField source="photo_required" />
          <BooleanField source="before_after_photo" />
          <BooleanField source="overall_measurement" />
          <BooleanField source="front_driver" />
          <BooleanField source="front_passenger" />
          <BooleanField source="rear_driver" />
          <BooleanField source="rear_passenger" />
          <BooleanField source="notes_required" />
          <BooleanField source="is_hidden" />

          <ConditionOptionsChipField
            source="condition_options"
            sortable={false}
          />

          {/* TODO: Link to technician blurb "show" page */}
          <ArrayField source="technician_blurbs" sortable={false}>
            <SingleFieldList linkType={false}>
              <ChipField source="name" />
            </SingleFieldList>
          </ArrayField>

          {/* TODO: Link to service offering "show" page */}
          <ArrayField source="suggested_service_offerings" sortable={false}>
            <SingleFieldList linkType={false}>
              <ChipField source="tiny_internal_name" />
            </SingleFieldList>
          </ArrayField>

          <EditButton />
        </ZippityDatagrid>
      </ListWithCSVImport>
      <p>These are the Inspections that form the Health Check.</p>
    </div>
  ),
);

const editStyles = {
  referenceArrayWide: { width: '100%' },
};

const validateRequiredString = [required(), minLength(2), maxLength(63)];
const validateString = [minLength(2), maxLength(63)];
const validateLongString = [minLength(2), maxLength(1023)];
const validateNumber = [required(), number()];

export const InspectionEdit = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Edit
      undoable={false}
      title={<CustomTitle title="resources.inspection.title" />}
      {...props}
    >
      <ZippitySimpleForm>
        <TextInput validate={validateRequiredString} source="inspection_name" />
        <NumberInput validate={validateNumber} source="display_order" />

        <TextInput validate={validateString} source="measurement_unit" />
        <TextInput
          multiline
          validate={validateLongString}
          source="technician_description"
        />
        <TextInput
          multiline
          validate={validateLongString}
          source="customer_description"
        />

        <BooleanInput source="condition_required" />

        <FormDataConsumer>
          {({ formData }) =>
            !!formData.condition_required && (
              <>
                <ReferenceInput
                  className={classes.referenceArrayWide}
                  source="condition_options_id"
                  reference="condition-options"
                  perPage={perPage}
                >
                  <ReferenceChild
                    {...props}
                    optionText={(o) => o.options.slice(0, 10).join(' | ')}
                    source="options"
                  />
                </ReferenceInput>
              </>
            )
          }
        </FormDataConsumer>

        <BooleanInput source="photo_required" />
        <BooleanInput source="before_after_photo" />
        <BooleanInput source="overall_measurement" />
        <BooleanInput source="front_driver" />
        <BooleanInput source="front_passenger" />
        <BooleanInput source="rear_driver" />
        <BooleanInput source="rear_passenger" />
        <BooleanInput source="notes_required" />
        <BooleanInput source="is_hidden" />

        {/* TODO: Allow edit */}
        <ArrayField source="technician_blurbs">
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ArrayField>

        <ReferenceArrayInput
          source="suggested_service_offering_ids"
          reference="service-offering"
          perPage={perPage}
          className={classes.referenceArrayWide}
          sort={{ field: 'tiny_internal_name', order: 'ASC' }}
        >
          <ReferenceArrayChild
            {...props}
            optionText="tiny_internal_name"
            source="suggested_service_offering_ids"
          />
        </ReferenceArrayInput>
      </ZippitySimpleForm>
    </Edit>
  ),
);

export const InspectionCreate = withStyles(editStyles)(
  ({ classes, ...props }) => (
    <Create {...props}>
      <ZippitySimpleForm>
        <TextInput validate={validateRequiredString} source="inspection_name" />
        <NumberInput validate={validateNumber} source="display_order" />

        <TextInput validate={validateString} source="measurement_unit" />
        <TextInput
          multiline
          validate={validateLongString}
          source="technician_description"
        />
        <TextInput
          multiline
          validate={validateLongString}
          source="customer_description"
        />

        <BooleanInput source="condition_required" />

        <FormDataConsumer>
          {({ formData }) =>
            !!formData.condition_required && (
              <>
                <ReferenceInput
                  className={classes.referenceArrayWide}
                  source="condition_options_id"
                  reference="condition-options"
                  perPage={perPage}
                >
                  <ReferenceChild
                    {...props}
                    optionText={(o) => o.options.slice(0, 10).join(' | ')}
                    source="options"
                  />
                </ReferenceInput>
              </>
            )
          }
        </FormDataConsumer>

        <BooleanInput source="photo_required" />
        <BooleanInput source="before_after_photo" />
        <BooleanInput source="overall_measurement" />
        <BooleanInput source="front_driver" />
        <BooleanInput source="front_passenger" />
        <BooleanInput source="rear_driver" />
        <BooleanInput source="rear_passenger" />
        <BooleanInput source="notes_required" />
        <BooleanInput source="is_hidden" />

        <ReferenceArrayInput
          source="suggested_service_offering_ids"
          reference="service-offering"
          perPage={perPage}
          className={classes.referenceArrayWide}
          sort={{ field: 'tiny_internal_name', order: 'ASC' }}
        >
          <ReferenceArrayChild
            {...props}
            optionText="tiny_internal_name"
            source="suggested_service_offering_ids"
          />
        </ReferenceArrayInput>
      </ZippitySimpleForm>
    </Create>
  ),
);
